import {AttendeeAnalyticsProps} from "./attendee-analytics.types";

/**
 * AttendeeAnalytics entity - row record for the attendees table.
 *
 * Contains the seven fields from v2 attendee response:
 * firstName, lastName, emailAddress, amount, currency, reference, createdAt.
 *
 * Pure TypeScript - no React imports.
 */
export class AttendeeAnalytics {
  private readonly props: AttendeeAnalyticsProps;

  private constructor(props: AttendeeAnalyticsProps) {
    this.props = props;
  }

  /**
   * Creates an AttendeeAnalytics entity with validation.
   * @throws Error if emailAddress or reference is empty
   * @throws Error if amount is negative
   */
  static create(props: AttendeeAnalyticsProps): AttendeeAnalytics {
    const emailAddress = (props.emailAddress ?? "").trim();
    const reference = (props.reference ?? "").trim();
    const amount = props.amount ?? 0;

    if (!emailAddress) {
      throw new Error("emailAddress cannot be empty");
    }
    if (!reference) {
      throw new Error("reference cannot be empty");
    }
    if (amount < 0) {
      throw new Error("amount cannot be negative");
    }

    return new AttendeeAnalytics({
      firstName: props.firstName ?? "",
      lastName: props.lastName ?? "",
      emailAddress,
      amount,
      currency: props.currency ?? "",
      reference,
      createdAt: props.createdAt,
      legacyId: props.legacyId,
    });
  }

  get firstName(): string {
    return this.props.firstName;
  }

  get lastName(): string {
    return this.props.lastName;
  }

  get emailAddress(): string {
    return this.props.emailAddress;
  }

  get amount(): number {
    return this.props.amount;
  }

  get currency(): string {
    return this.props.currency;
  }

  get reference(): string {
    return this.props.reference;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  /**
   * Returns the full name (firstName + lastName), trimmed.
   */
  get fullName(): string {
    return `${this.props.firstName} ${this.props.lastName}`.trim();
  }

  /**
   * Returns true if the attendee has paid (amount > 0).
   */
  isPaid(): boolean {
    return this.props.amount > 0;
  }

  /**
   * Returns a stable React-list key (the reference, which is unique per attendee).
   */
  get key(): string {
    return this.props.reference;
  }

  /**
   * V1-only: numeric attendee ID needed for v1 details endpoint.
   * Returns undefined for v2 attendees.
   */
  get legacyId(): number | undefined {
    return this.props.legacyId;
  }
}
